import React from "react";
import { motion } from "framer-motion";
import Footer from "./Footer";

const sections = [
  {
    title: "Introduction",
    content:
      'Welcome to Axtelica Solutions Pvt Ltd ("Axtelica", "Company", "we", "our", or "us"). These Terms and Conditions ("Terms") govern your access to and use of our website, software products, platforms, applications, consulting services, AI solutions, and related services operated by Axtelica. By accessing or using our services, websites, or platforms, you agree to comply with and be bound by these Terms. If you do not agree with these Terms, please do not use our services.',
  },
  {
    title: "About Axtelica",
    content:
      "Axtelica Solutions Pvt Ltd is a technology company providing Artificial Intelligence (AI) solutions, SaaS products, ERP and CRM development, cloud-based platforms, business automation systems, mobile and web application development, data analytics and business intelligence solutions, enterprise software services, and IT consulting and technology support. Axtelica may develop and operate proprietary products, platforms, and digital services, including AI-powered software solutions such as Axtream and other future products.",
  },
  {
    title: "Acceptance of Terms",
    content:
      "These Terms apply to website visitors, clients and customers, trial users, subscribers, business partners, platform users, and organizations accessing our products or services. By using our services, you confirm that you are legally authorized to enter into this agreement, the information you provide is accurate and complete, and you will comply with applicable laws and regulations.",
  },
  {
    title: "Services and Products",
    content:
      "Axtelica provides software products, SaaS platforms, AI-powered tools, and custom technology services. Certain services may require user registration, subscription plans, project agreements, service contracts, or separate commercial proposals or Statements of Work (SOW). Specific terms applicable to a product or service may be provided separately and shall form part of these Terms.",
  },
  {
    title: "User Responsibilities",
    content:
      "You agree to use our services only for lawful purposes, maintain confidentiality of credentials, provide accurate information, avoid misuse of systems or infrastructure, respect intellectual property rights, and comply with all applicable laws and industry regulations. You are responsible for all activities conducted through your account or systems.",
  },
  {
    title: "Intellectual Property Rights",
    content:
      "All intellectual property including software, source code, AI models, designs, branding, logos, documentation, platforms, APIs, content, and user interfaces are exclusive property of Axtelica Solutions Pvt Ltd or its licensors. No ownership rights are transferred. You may not copy, modify, distribute, reverse engineer, resell, or republish any part without written permission.",
  },
  {
    title: "Data and Confidentiality",
    content:
      "Clients and users retain ownership of their data. By using services, you grant us limited rights to process, store, and analyze data to deliver services. We implement commercially reasonable security measures, but no system guarantees absolute security. Both parties agree to maintain confidentiality of sensitive business information.",
  },
  {
    title: "AI and Technology Disclaimer",
    content:
      "AI-generated outputs, recommendations, analytics, automations, or predictive insights are provided for informational and operational assistance only. Axtelica does not guarantee accuracy, completeness, reliability, error-free results, or business outcomes. Users must independently validate outputs before decisions.",
  },
  {
    title: "Payments and Billing",
    content:
      "Payments must follow agreed commercial terms. Delayed payments may result in suspension of services. Taxes and applicable government charges are client responsibility unless stated otherwise. Subscription services may renew automatically unless canceled. Refunds are governed by separate agreements.",
  },
  {
    title: "Third-Party Services",
    content:
      "Our platforms may integrate third-party tools, APIs, hosting providers, payment gateways, or software services. Axtelica is not responsible for third-party availability, failures, data policies, or damages caused by external providers.",
  },
  {
    title: "Prohibited Activities",
    content:
      "Users must not attempt unauthorized access, introduce malware, interfere with systems, abuse APIs, use services for illegal activities, violate cybersecurity laws, scrape protected data, or misrepresent affiliation with Axtelica. Violations may result in suspension or legal action.",
  },
  {
    title: "Service Availability",
    content:
      "We strive for reliable services but downtime may occur due to maintenance, technical failures, or upgrades. Features may change or be discontinued at any time.",
  },
  {
    title: "Limitation of Liability",
    content:
      "To the maximum extent permitted by law, Axtelica is not liable for indirect damages, loss of profits, data loss, business interruption, security incidents, or AI inaccuracies. Total liability shall not exceed amount paid in last 12 months.",
  },
  {
    title: "Indemnification",
    content:
      "You agree to indemnify and hold harmless Axtelica, its directors, employees, and affiliates against claims arising from misuse of services, violation of Terms, or legal breaches.",
  },
  {
    title: "Termination",
    content:
      "We may suspend or terminate access if Terms are violated, payments are overdue, or security risks are detected. Clients may terminate services subject to contractual obligations.",
  },
  {
    title: "Governing Law",
    content:
      "These Terms are governed by laws of India. Disputes shall be subject to exclusive jurisdiction of courts in Hyderabad, Telangana, India.",
  },
  {
    title: "Changes to Terms",
    content:
      "We may update Terms at any time. Continued use of services constitutes acceptance of revised Terms.",
  },
  {
    title: "Contact Information",
    content: (
      <>
        <p className="mb-2">Axtelica Solutions Pvt Ltd.</p>

        <p className="mb-2 break-all">
          Email:{" "}
          <a
            href="mailto:info@axtelica.com"
            className="text-blue-600 hover:text-blue-800 underline transition"
          >
            info@axtelica.com
          </a>
        </p>

        <p className="break-all">
          Website:{" "}
          <a
            href="https://axtelica.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800 underline transition"
          >
            Axtelica Official Website
          </a>
        </p>
      </>
    ),
  },
];

export default function TermAndCondition() {
  const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  };

  return (
    <div className="w-full overflow-hidden bg-white">
      {/* HERO SECTION */}
      <section
        className="
          relative flex items-center justify-center
          py-14 sm:py-16 lg:py-20
          min-h-[240px] sm:min-h-[320px] lg:min-h-[420px]
          w-full overflow-hidden
          bg-[linear-gradient(-45deg,#101030,#0F1242,#2558BA,#306BCA)]
          bg-[length:400%_400%]
          animate-gradientFlow
        "
      >
        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate="show"
          className="
            relative z-10
            w-full
            max-w-[1400px]
            px-4 sm:px-6 lg:px-8
            flex justify-center
          "
        >
          <h1
            className="
              text-[30px]
              sm:text-[42px]
              md:text-[52px]
              lg:text-[64px]
              font-semibold
              text-white
              text-center
              leading-tight
              max-w-4xl
            "
          >
            Terms & Conditions
          </h1>
        </motion.div>
      </section>

      {/* MAIN CONTENT */}
      <div className="w-full py-8 sm:py-10 lg:py-14 px-3 sm:px-5 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="
            max-w-[1400px]
            mx-auto
            rounded-2xl
            px-6
          "
        >
          {/* SUBTITLE */}
          <p
            className="
              text-center
              text-gray-700
              text-base
              sm:text-lg
              md:text-xl
              lg:text-2xl
              leading-relaxed
              mb-8
              sm:mb-10
              px-2
            "
          >
            Please read these terms carefully before using our services.
          </p>

          {/* TERMS SECTIONS */}
          <div className="space-y-5 sm:space-y-6">
            {sections.map((sec, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{
                  duration: 0.4,
                  delay: index * 0.05,
                }}
                className="
                  border border-gray-200
                  rounded-2xl
                  p-4
                  sm:p-6
                  lg:p-7
                  bg-white
                  hover:shadow-lg
                  transition-all
                  duration-300
                "
              >
                {/* SECTION TITLE */}
                <h2
                  className="
                    text-xl
                    sm:text-2xl
                    lg:text-2xl
                    
                    text-gray-800
                    mb-3
                    leading-snug
                  "
                >
                  {sec.title}
                </h2>

                {/* SECTION CONTENT */}
                <div
                  className="
                    text-sm
                    sm:text-base
                    lg:text-lg
                    text-gray-600
                    leading-7
                    sm:leading-8
                    whitespace-pre-line
                    break-words
                  "
                >
                  {sec.content}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>

      <Footer />
    </div>
  );
}